import React, { useEffect, useState } from "react";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../lib/CartContext";
import { api } from "../lib/api";
import { PayPalCardNote } from "./PayPalCardNote";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${backend}${u}` : u);

export default function CartDrawer() {
  const cart = useCart();
  const { items, isOpen, closeCart, updateQty, removeItem, lineKey, totals, clear, SHIPPING_FLAT } = cart;
  const [checkingOut, setCheckingOut] = useState(false);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (isOpen) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = orig; };
    }
  }, [isOpen]);

  const createOrder = (_data, actions) => {
    if (items.length === 0) return Promise.reject(new Error("Cart empty"));
    return actions.order.create({
      intent: "CAPTURE",
      purchase_units: [{
        description: `When Pigs Fly Family BBQ — ${totals.count} item${totals.count === 1 ? "" : "s"}`,
        amount: {
          currency_code: "USD",
          value: totals.grandTotalStr,
          breakdown: {
            item_total: { currency_code: "USD", value: totals.itemsTotalStr },
            shipping: { currency_code: "USD", value: totals.shippingStr },
            tax_total: { currency_code: "USD", value: totals.taxStr },
          },
        },
        items: items.map((it) => ({
          name: (it.variant ? `${it.name} (${it.variant})` : it.name).slice(0, 127),
          quantity: String(it.qty),
          unit_amount: { currency_code: "USD", value: it.price.toFixed(2) },
          category: "PHYSICAL_GOODS",
        })),
      }],
      application_context: {
        shipping_preference: "GET_FROM_FILE", // PayPal collects shipping address in-flow
        brand_name: "When Pigs Fly Family BBQ",
        user_action: "PAY_NOW",
      },
    });
  };

  const onApprove = async (_data, actions) => {
    setCheckingOut(true);
    try {
      const details = await actions.order.capture();
      const payer = details?.payer;
      const payerName = payer?.name ? `${payer.name.given_name || ""} ${payer.name.surname || ""}`.trim() : "";
      // Record the confirmed order (also fires web push + Twilio SMS to the owner).
      try {
        await api.post("/orders", {
          paypal_order_id: details?.id || _data?.orderID || "",
          items: items.map((it) => ({ slug: it.slug, name: it.name, price: it.price, qty: it.qty, variant: it.variant || "" })),
          subtotal: totals.itemsTotal,
          shipping: totals.shipping,
          tax: totals.tax,
          total: totals.grandTotal,
          payer_name: payerName,
          payer_email: payer?.email_address || "",
          shipping_address: details?.purchase_units?.[0]?.shipping || null,
        });
      } catch (e) { console.error("save order failed", e); }
      toast.success(`Thanks ${payer?.name?.given_name || "friend"}! Your order is confirmed. A receipt is on the way.`);
      clear();
      closeCart();
    } catch (e) {
      console.error("Capture failed", e);
      toast.error("Payment couldn't be captured. Please try again or call the pit.");
      _saveAbandonedCart("error");
    } finally { setCheckingOut(false); }
  };

  const _saveAbandonedCart = async (reason) => {
    if (items.length === 0) return;
    try {
      await api.post("/abandoned-carts", {
        items: items.map((it) => ({ slug: it.slug, name: it.name, price: it.price, qty: it.qty, variant: it.variant || "" })),
        subtotal: totals.itemsTotal,
        shipping: totals.shipping,
        tax: totals.tax,
        total: totals.grandTotal,
        reason,
      });
    } catch (e) { console.error("save abandoned cart failed", e); }
  };

  const onError = (err) => {
    console.error("PayPal error", err);
    toast.error("PayPal ran into an issue. Try again in a moment.");
    _saveAbandonedCart("error");
  };

  const onCancel = () => {
    toast("Checkout cancelled", { icon: "🐷" });
    _saveAbandonedCart("cancel");
  };

  return (
    <>
      {/* Overlay */}
      <div
        aria-hidden={!isOpen}
        onClick={closeCart}
        className={`fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />
      {/* Drawer — full width on phones, capped at ~448px on tablet+ */}
      <aside
        role="dialog"
        aria-label="Shopping cart"
        aria-hidden={!isOpen}
        data-testid="cart-drawer"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
        className={`fixed right-0 top-0 z-[70] flex h-[100dvh] w-full flex-col border-l-2 border-ink bg-bone shadow-hard transition-transform duration-300 sm:max-w-md ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header — sticky */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b-2 border-ink bg-white px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} className="shrink-0" />
            <h2 className="font-alfa text-lg uppercase leading-none sm:text-xl">Your Cart</h2>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink/60">({totals.count})</span>
          </div>
          <button
            type="button"
            onClick={closeCart}
            data-testid="cart-close"
            aria-label="Close cart"
            className="border border-ink p-2 hover:bg-ink hover:text-bone"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable body — items, totals, PayPal button all in one column so PayPal's inline
             address/card form (guest checkout) can expand without being clipped. */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="px-4 py-4 sm:px-6">
            {items.length === 0 ? (
              <div className="mt-16 text-center" data-testid="cart-empty">
                <ShoppingBag size={40} className="mx-auto text-ink/30" />
                <p className="mt-4 font-alfa text-lg uppercase">Cart's empty</p>
                <p className="mt-1 font-serif text-sm text-ink/60">Add a rub, sauce, or tee and it'll show up here.</p>
              </div>
            ) : (
              <ul className="space-y-3">
                {items.map((it) => {
                  const key = lineKey(it);
                  return (
                    <li key={key} className="flex gap-3 border border-ink bg-white p-3" data-testid={`cart-line-${it.slug}`}>
                      {it.image && (
                        <img
                          src={resolveImg(it.image)}
                          alt={it.name}
                          className="h-16 w-16 flex-shrink-0 border border-ink/30 bg-white object-contain sm:h-20 sm:w-20"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-alfa text-sm uppercase leading-tight break-words">{it.name}</p>
                        {it.variant && (
                          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Size: {it.variant}</p>
                        )}
                        <p className="mt-1 font-mono text-sm font-bold">${(it.price * it.qty).toFixed(2)}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateQty(key, it.qty - 1)}
                            disabled={it.qty <= 1}
                            data-testid={`cart-dec-${it.slug}`}
                            aria-label="Decrease quantity"
                            className="border border-ink p-1.5 disabled:opacity-30 hover:bg-ink hover:text-bone"
                          >
                            <Minus size={12} />
                          </button>
                          <span data-testid={`cart-qty-${it.slug}`} className="min-w-[24px] text-center font-mono text-sm">{it.qty}</span>
                          <button
                            type="button"
                            onClick={() => updateQty(key, it.qty + 1)}
                            data-testid={`cart-inc-${it.slug}`}
                            aria-label="Increase quantity"
                            className="border border-ink p-1.5 hover:bg-ink hover:text-bone"
                          >
                            <Plus size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeItem(key)}
                            data-testid={`cart-remove-${it.slug}`}
                            aria-label="Remove"
                            className="ml-auto border border-ink p-1.5 hover:bg-red-700 hover:text-bone hover:border-red-700"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t-2 border-ink bg-white px-4 py-4 sm:px-6 sm:py-6">
              <dl className="space-y-1 font-mono text-sm">
                <div className="flex justify-between">
                  <dt className="text-ink/60">Subtotal</dt>
                  <dd data-testid="cart-subtotal">${totals.itemsTotalStr}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ink/60">Shipping &amp; Handling</dt>
                  <dd data-testid="cart-shipping" className={totals.freeShipping ? "font-bold text-red-700" : ""}>
                    {totals.freeShipping ? "FREE" : `$${totals.shippingStr}`}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ink/60">Sales Tax (7%)</dt>
                  <dd data-testid="cart-tax">${totals.taxStr}</dd>
                </div>
                <div className="mt-2 flex justify-between border-t border-ink pt-2 text-base font-bold">
                  <dt>Total</dt>
                  <dd data-testid="cart-total">${totals.grandTotalStr}</dd>
                </div>
              </dl>
              {/* Free shipping progress / earned banner */}
              {totals.freeShipping ? (
                <div data-testid="cart-free-shipping-badge" className="mt-3 border-2 border-red-700 bg-red-50 px-3 py-2 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-red-700">
                  🎉 You unlocked FREE shipping!
                </div>
              ) : (
                <div data-testid="cart-free-shipping-progress" className="mt-3 border border-ink/40 bg-bone px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                  <div className="flex justify-between">
                    <span>Add ${totals.amountToFreeShipping.toFixed(2)} more</span>
                    <span className="text-ink/60">for FREE shipping</span>
                  </div>
                  <div className="mt-2 h-1 w-full overflow-hidden border border-ink/30 bg-white">
                    <div
                      className="h-full bg-ink transition-all"
                      style={{ width: `${Math.min(100, (totals.itemsTotal / totals.freeShippingThreshold) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
                {totals.freeShipping ? "FREE shipping on orders over $25" : `Flat $${SHIPPING_FLAT.toFixed(2)} shipping · Free over $25`} · Receipt emailed by PayPal
              </p>
              {/* PayPal buttons — NO overflow clipping so the inline address/card form
                   from guest checkout can expand and be scrolled with the drawer body. */}
              <div className="mt-3 w-full" data-testid="cart-paypal-button">
                <PayPalButtons
                  key={`cart-${items.length}-${totals.grandTotalStr}`}
                  style={{ layout: "vertical", shape: "rect", color: "black", label: "checkout", height: 45 }}
                  disabled={checkingOut}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onError={onError}
                  onCancel={onCancel}
                />
                <PayPalCardNote testid="cart-card-note" />
              </div>
              <button
                type="button"
                onClick={closeCart}
                data-testid="cart-continue-shopping"
                className="mt-2 w-full border border-ink bg-bone px-4 py-2 font-mono text-[11px] uppercase tracking-[0.24em] hover:bg-ink hover:text-bone"
              >
                Keep shopping
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
